import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import * as NotificationSelectors from '../../store/notification/selectors/notification.selectors';
import * as NotificationActions from '../../store/notification/actions/notification.actions';
import {
  Notification,
  NotificationAction,
} from '../../store/notification/actions/notification.actions';
import { animate, style, transition, trigger } from '@angular/animations';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-notification-toast',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './notification-toast.component.html',
  styleUrls: ['./notification-toast.component.css'],
  animations: [
    trigger('slideIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-20px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
    trigger('fadeOut', [transition(':leave', [animate('300ms ease-out', style({ opacity: 0 }))])]),
  ],
})
export class NotificationToastComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  private destroy$ = new Subject<void>();
  private timers = new Map<string, any>();
  private countdownTimers = new Map<string, any>();

  // Signal for notifications
  notifications = signal<Notification[]>([]);

  // Signal for tracking remaining time for each notification (in milliseconds)
  remainingTimes = signal<Map<string, number>>(new Map());

  // Computed signal for expanded state tracking
  expandedStates = signal<Map<string, boolean>>(new Map());

  // Computed signal to check if any notification is blocking
  hasBlockingNotifications = computed(() => this.notifications().some((n) => n.blocking === true));

  // Computed signals for blocking and non-blocking notifications
  blockingNotifications = computed(() => this.notifications().filter((n) => n.blocking === true));

  nonBlockingNotifications = computed(() =>
    this.notifications().filter((n) => n.blocking !== true),
  );

  ngOnInit(): void {
    // Subscribe to notifications from store
    this.store
      .select(NotificationSelectors.selectAllNotifications)
      .pipe(takeUntil(this.destroy$))
      .subscribe((notifications) => {
        this.notifications.set(notifications);

        // Setup auto-dismiss timers and countdown timers for new notifications
        notifications.forEach((notification) => {
          if (
            notification.duration &&
            notification.duration > 0 &&
            !this.timers.has(notification.id)
          ) {
            // Initialize remaining time
            const currentTimes = this.remainingTimes();
            const newTimes = new Map(currentTimes);
            newTimes.set(notification.id, notification.duration);
            this.remainingTimes.set(newTimes);

            // Auto-dismiss timer
            const timer = setTimeout(() => {
              this.dismiss(notification.id);
              this.timers.delete(notification.id);
            }, notification.duration);
            this.timers.set(notification.id, timer);

            // Countdown timer (updates every 100ms for smooth countdown)
            const countdownTimer = setInterval(() => {
              const times = this.remainingTimes();
              const remaining = times.get(notification.id);
              if (remaining !== undefined && remaining > 0) {
                const updatedTimes = new Map(times);
                updatedTimes.set(notification.id, Math.max(0, remaining - 100));
                this.remainingTimes.set(updatedTimes);
              }
            }, 100);
            this.countdownTimers.set(notification.id, countdownTimer);
          }
        });

        // Clean up timers for dismissed notifications
        const currentIds = new Set(notifications.map((n) => n.id));
        this.timers.forEach((timer, id) => {
          if (!currentIds.has(id)) {
            clearTimeout(timer);
            this.timers.delete(id);
          }
        });
        this.countdownTimers.forEach((timer, id) => {
          if (!currentIds.has(id)) {
            clearInterval(timer);
            this.countdownTimers.delete(id);
          }
        });
      });
  }

  ngOnDestroy(): void {
    // Clear all timers
    this.timers.forEach((timer) => clearTimeout(timer));
    this.timers.clear();
    this.countdownTimers.forEach((timer) => clearInterval(timer));
    this.countdownTimers.clear();

    this.destroy$.next();
    this.destroy$.complete();
  }

  dismiss(id: string): void {
    // Clear timers if exists
    const timer = this.timers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(id);
    }

    const countdownTimer = this.countdownTimers.get(id);
    if (countdownTimer) {
      clearInterval(countdownTimer);
      this.countdownTimers.delete(id);
    }

    // Remove from remaining times
    const times = this.remainingTimes();
    const newTimes = new Map(times);
    newTimes.delete(id);
    this.remainingTimes.set(newTimes);

    this.store.dispatch(NotificationActions.DismissNotification({ id }));
  }

  stopTimer(id: string): void {
    const timer = this.timers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(id);
    }

    const countdownTimer = this.countdownTimers.get(id);
    if (countdownTimer) {
      clearInterval(countdownTimer);
      this.countdownTimers.delete(id);
    }

    // Remove from remaining times
    const times = this.remainingTimes();
    const newTimes = new Map(times);
    newTimes.delete(id);
    this.remainingTimes.set(newTimes);
  }

  getRemainingSeconds(id: string): number {
    const remaining = this.remainingTimes().get(id);
    return remaining ? Math.ceil(remaining / 1000) : 0;
  }

  getProgressPercentage(notification: Notification): number {
    if (!notification.duration || notification.duration === 0) {
      return 100;
    }
    const remaining = this.remainingTimes().get(notification.id);
    if (remaining === undefined) {
      return 100;
    }
    const percentage = (remaining / notification.duration) * 100;
    return Math.max(0, Math.min(100, percentage));
  }

  toggleExpand(id: string): void {
    const currentStates = this.expandedStates();
    const newStates = new Map(currentStates);
    const isExpanded = newStates.get(id) || false;
    newStates.set(id, !isExpanded);
    this.expandedStates.set(newStates);
  }

  isExpanded(id: string): boolean {
    return this.expandedStates().get(id) || false;
  }

  handleAction(action: NotificationAction, notificationId: string): void {
    // Dispatch the custom action
    this.store.dispatch({
      type: action.actionType,
      ...action.payload,
    });

    // Dismiss the notification
    this.dismiss(notificationId);
  }

  getIcon(severity: string, customIcon?: string): string {
    // Use custom icon if provided
    if (customIcon) {
      return customIcon;
    }

    switch (severity) {
      case 'success':
        return 'pi pi-check-circle';
      case 'error':
        return 'pi pi-exclamation-circle';
      case 'warning':
        return 'pi pi-exclamation-triangle';
      case 'info':
        return 'pi pi-info-circle';
      default:
        return 'pi pi-info-circle';
    }
  }

  getIconColor(severity: string): string {
    switch (severity) {
      case 'success':
        return 'text-green-500';
      case 'error':
        return 'text-red-500';
      case 'warning':
        return 'text-yellow-500';
      case 'info':
        return 'text-blue-500';
      default:
        return 'text-blue-500';
    }
  }

  getBorderColor(severity: string): string {
    switch (severity) {
      case 'success':
        return 'border-green-500';
      case 'error':
        return 'border-red-500';
      case 'warning':
        return 'border-yellow-500';
      case 'info':
        return 'border-blue-500';
      default:
        return 'border-blue-500';
    }
  }

  getProgressColor(severity: string): string {
    switch (severity) {
      case 'success':
        return 'bg-green-500';
      case 'error':
        return 'bg-red-500';
      case 'warning':
        return 'bg-yellow-500';
      case 'info':
        return 'bg-blue-500';
      default:
        return 'bg-blue-500';
    }
  }

  /**
   * Get position classes from environment configuration
   */
  getPositionClasses(): string {
    const position = environment.notification.position;
    const baseClasses = 'fixed z-50 flex flex-col gap-3 w-96 max-w-[90vw]';

    switch (position) {
      case 'top-right':
        return `${baseClasses} top-4 right-4`;
      case 'top-left':
        return `${baseClasses} top-4 left-4`;
      case 'top-center':
        return `${baseClasses} top-4 left-1/2 -translate-x-1/2`;
      case 'bottom-right':
        return `${baseClasses} bottom-4 right-4`;
      case 'bottom-left':
        return `${baseClasses} bottom-4 left-4`;
      case 'bottom-center':
        return `${baseClasses} bottom-4 left-1/2 -translate-x-1/2`;
      case 'center-center':
        return `${baseClasses} top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2`;
      default:
        return `${baseClasses} top-4 right-4`;
    }
  }
}
